import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef, useMemo } from 'react';
import {
    Stack, Typography, TextField, Box, IconButton, Chip,
    Alert, Collapse, Button, CircularProgress, Tabs, Tab,
    Autocomplete, Snackbar, Select, MenuItem, FormControl, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SendIcon from '@mui/icons-material/Send';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LinkIcon from '@mui/icons-material/Link';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import Api from '../Services/Api';
import VoiceInput from '../record/components/VoiceInput';
import { COLLAPSED_HEIGHT } from '../components/Layout';
import {
    glassCard, ACCENT, ACCENT_DARK, ACCENT_GLOW,
    TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, TEXT_DISABLED, TEXT_HINT,
    SURFACE_BORDER, SURFACE_HOVER, SURFACE_ACTIVE, noMotion
} from '../designsystem/tokens';
import {
    OBJECT_TYPES, TYPE_COLORS, inputSx, darkPopperProps, selectMenuProps
} from './propertyUtils';
import useManualPropertyConfig from './propertyChat/useManualPropertyConfig';
import usePropertyChatSocket from './propertyChat/usePropertyChatSocket';
import { asArray, normalizeActions, normalizePreview, previewFromActions } from './propertyChat/contracts';

const transition = 'all 200ms ease-out';

function MarkdownMessage({ content, textColor = TEXT_PRIMARY }) {
    return (
        <Box
            sx={{
                color: textColor,
                '& p': { m: 0, mb: 1, lineHeight: 1.55, fontSize: '0.9rem' },
                '& p:last-of-type': { mb: 0 },
                '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
                '& li': { mb: 0.4 },
                '& li > p': { mb: 0 },
                '& a': { color: ACCENT, textDecoration: 'underline' },
                '& strong': { color: TEXT_PRIMARY, fontWeight: 700 },
                '& em': { color: TEXT_SECONDARY },
                '& h1, & h2, & h3': { m: 0, mb: 0.75, lineHeight: 1.3, fontWeight: 700, color: TEXT_PRIMARY },
                '& h1': { fontSize: '1rem' },
                '& h2': { fontSize: '0.96rem' },
                '& h3': { fontSize: '0.92rem' },
                '& blockquote': {
                    m: 0, mb: 1, pl: 1.25, borderLeft: `3px solid ${ACCENT}`,
                    color: TEXT_SECONDARY, fontStyle: 'italic',
                },
                '& code': {
                    px: 0.5, py: 0.15, borderRadius: '4px',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    fontSize: '0.78rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                },
                '& pre': {
                    m: 0, mb: 1, p: 1.25, borderRadius: '10px', overflowX: 'auto',
                    backgroundColor: 'rgba(0,0,0,0.28)',
                    border: '1px solid rgba(255,255,255,0.08)',
                },
                '& pre code': {
                    p: 0, backgroundColor: 'transparent', display: 'block',
                    fontSize: '0.76rem', lineHeight: 1.55,
                },
                '& hr': { border: 0, borderTop: '1px solid rgba(255,255,255,0.12)', my: 1 },
            }}
        >
            <ReactMarkdown>{typeof content === 'string' ? content : ''}</ReactMarkdown>
        </Box>
    );
}

// ─── PropertyPreviewCard (for Chat tab) ──────────────────────────
function PropertyPreviewCard({ objectType, properties, onToggleMandatory, onRemove, onChangeMandatoryIf, allPropertyNames, editable = true }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);
    const safeProperties = asArray(properties);

    return (
        <Box sx={{ ...glassCard, mt: 1, p: 1.5 }}>
            <Box
                role="button"
                tabIndex={0}
                data-testid={`preview-card-${objectType}`}
                aria-label={`${t(objectType)} ${t('propertiesFor')}`}
                onClick={() => setExpanded(!expanded)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}
                sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', minHeight: 44, px: 0.5,
                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, borderRadius: '8px' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={t(objectType)}
                        size="small"
                        sx={{ backgroundColor: ACCENT_DARK, color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}
                    />
                    <Typography variant="caption" sx={{ color: TEXT_TERTIARY }}>
                        {safeProperties.length} {safeProperties.length === 1 ? t('propertySingular', 'property') : t('propertyPlural', 'properties')}
                    </Typography>
                </Box>
                {expanded ? <ExpandLessIcon sx={{ color: TEXT_TERTIARY, fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: TEXT_TERTIARY, fontSize: 20 }} />}
            </Box>
            <Collapse in={expanded}>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {safeProperties.map((prop) => (
                        <Box
                            key={prop.name}
                            data-testid={`prop-row-${objectType}-${prop.name}`}
                            sx={{
                                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                                py: 0.75, px: 1, borderRadius: '10px',
                                backgroundColor: 'rgba(255,255,255,0.03)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                                transition, ...noMotion,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 500, lineHeight: 1.4 }}>
                                        {prop.label || prop.name}
                                    </Typography>
                                    <Chip
                                        label={prop.type || prop.fieldType || 'text'}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem', height: 20,
                                            backgroundColor: (TYPE_COLORS[prop.type] || TYPE_COLORS[prop.fieldType] || '#78909C') + '22',
                                            color: TYPE_COLORS[prop.type] || TYPE_COLORS[prop.fieldType] || '#78909C',
                                            border: `1px solid ${(TYPE_COLORS[prop.type] || TYPE_COLORS[prop.fieldType] || '#78909C')}44`,
                                            flexShrink: 0,
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                {editable ? (
                                    <IconButton
                                        size="small"
                                        data-testid={`toggle-mandatory-${objectType}-${prop.name}`}
                                        aria-label={prop.mandatory ? t('mandatoryBadge') : t('optionalBadge')}
                                        onClick={() => onToggleMandatory(objectType, prop.name)}
                                        sx={{ minWidth: 36, minHeight: 36, color: prop.mandatory ? ACCENT : TEXT_TERTIARY }}
                                    >
                                        {prop.mandatory ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                ) : (
                                    <Chip
                                        label={prop.mandatory ? t('mandatoryBadge') : (prop.mandatoryIf ? t('conditional') : t('optionalBadge'))}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem', height: 20,
                                            backgroundColor: prop.mandatory ? `${ACCENT}33` : 'rgba(255,255,255,0.08)',
                                            color: prop.mandatory ? ACCENT : TEXT_SECONDARY,
                                        }}
                                    />
                                )}
                                {editable && (
                                    <IconButton
                                        size="small"
                                        data-testid={`remove-prop-${objectType}-${prop.name}`}
                                        aria-label={`${t('remove')} ${prop.label || prop.name}`}
                                        onClick={() => onRemove(objectType, prop.name)}
                                        sx={{ minWidth: 36, minHeight: 36, color: TEXT_TERTIARY, '&:hover': { color: '#ef5350' } }}
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </Collapse>
        </Box>
    );
}

// ─── PropertyRow (compact expandable row for Manual tab) ─────────
function PropertyRow({ prop, idx, activeProps, onToggleMandatory, onChangeMandatoryIf, onRemove }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(!!prop.mandatoryIf);

    const typeBadgeColor = TYPE_COLORS[prop.type] || '#78909C';

    return (
        <Box data-testid={`prop-row-${prop.name}`} sx={{
            borderRadius: '10px',
            backgroundColor: 'rgba(255,255,255,0.03)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            transition, ...noMotion,
        }}>
            {/* Main row: ~48px */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, minHeight: 48, gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 500 }} noWrap>
                            {prop.label || prop.name}
                        </Typography>
                        {prop.type && (
                            <Chip
                                label={t(`propertyType_${prop.type}`, prop.type)}
                                size="small"
                                sx={{
                                    fontSize: '0.65rem', height: 20,
                                    backgroundColor: `${typeBadgeColor}22`,
                                    color: typeBadgeColor,
                                    border: `1px solid ${typeBadgeColor}44`,
                                    borderRadius: '4px', flexShrink: 0,
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        )}
                    </Stack>
                </Box>
                <IconButton
                    size="small"
                    onClick={() => onToggleMandatory(idx)}
                    aria-label={prop.mandatory ? t('mandatoryBadge') : t('optionalBadge')}
                    data-testid={`property-mandatory-${prop.name}`}
                    sx={{ minWidth: 44, minHeight: 44, color: prop.mandatory ? ACCENT : TEXT_TERTIARY }}
                >
                    {prop.mandatory ? <LockIcon sx={{ fontSize: 18 }} /> : <LockOpenIcon sx={{ fontSize: 18 }} />}
                </IconButton>
                {!prop.mandatory && (
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        aria-label={t('mandatoryIf')}
                        sx={{ minWidth: 44, minHeight: 44, color: prop.mandatoryIf ? ACCENT : TEXT_TERTIARY }}
                    >
                        {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                )}
                <IconButton
                    size="small"
                    onClick={() => onRemove(idx)}
                    aria-label={`${t('remove')} ${prop.label || prop.name}`}
                    data-testid={`property-remove-${prop.name}`}
                    sx={{ minWidth: 44, minHeight: 44, color: TEXT_TERTIARY, '&:hover': { color: '#ef5350' } }}
                >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            {/* Expandable mandatoryIf */}
            <Collapse in={expanded && !prop.mandatory}>
                <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
                    <FormControl fullWidth size="small">
                        <Select
                            value={prop.mandatoryIf || ''}
                            displayEmpty
                            onChange={(e) => onChangeMandatoryIf(idx, e.target.value)}
                            aria-label={`${t('mandatoryIf')} — ${prop.label || prop.name}`}
                            data-testid={`property-mandatoryif-${prop.name}`}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return (
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: TEXT_DISABLED }}>
                                            <LinkIcon sx={{ fontSize: 14 }} /><span>{t('mandatoryIf')}</span>
                                        </Stack>
                                    );
                                }
                                const dep = activeProps.find(p => p.name === selected);
                                return (
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <LinkIcon sx={{ fontSize: 14, color: ACCENT }} /><span>{dep?.label || selected}</span>
                                    </Stack>
                                );
                            }}
                            sx={{
                                color: 'white',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
                                '.MuiSvgIcon-root': { color: TEXT_HINT },
                            }}
                            MenuProps={selectMenuProps}
                        >
                            <MenuItem value=""><em>{t('noneCondition')}</em></MenuItem>
                            {activeProps.filter(p => p.name !== prop.name).map(p => (
                                <MenuItem key={p.name} value={p.name}>{p.label || p.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Collapse>
        </Box>
    );
}

// ─── ManualPanel ────────────────────────────────────────────────
function ManualPanel() {
    const { t } = useTranslation();
    const {
        selectedType,
        loading,
        dealTab,
        pipelines,
        selectedPipeline,
        selectedStage,
        stageLoading,
        isStageMode,
        activeProps,
        canSave,
        stageKey,
        success,
        saveError,
        saving,
        allProperties,
        loadTypeData,
        setDealTab,
        setSelectedPipeline,
        setSelectedStage,
        loadStageProperties,
        onSelectPipeline,
        onSelectStage,
        handleAddProperty,
        removeProperty,
        toggleMandatory,
        changeMandatoryIf,
        handleSave,
    } = useManualPropertyConfig();

    const reduceMotionSx = useMemo(() => ({
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none !important',
            animation: 'none !important',
        },
    }), []);

    return (
        <Stack spacing={2} sx={{ pb: 10 }}>
            {/* Alerts */}
            <Collapse in={success}>
                <Alert severity="success" variant="outlined" data-testid="manual-success-alert" sx={{ borderRadius: '12px', color: '#81c784', borderColor: '#81c784', backgroundColor: 'rgba(129,199,132,0.08)' }}>
                    {t('propertiesUpdated')}
                </Alert>
            </Collapse>
            <Collapse in={saveError}>
                <Alert severity="error" icon={<ErrorOutlineIcon />} variant="outlined" data-testid="manual-error-alert" sx={{ borderRadius: '12px', color: '#ef5350', borderColor: '#ef5350', backgroundColor: 'rgba(239,83,80,0.08)' }}>
                    {t('errorSavingProperties')}
                </Alert>
            </Collapse>

            {/* Object type chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {OBJECT_TYPES.map(type => (
                    <Chip
                        key={type}
                        label={t(type)}
                        data-testid={`manual-type-${type}`}
                        aria-label={t(type)}
                        aria-current={selectedType === type ? 'true' : undefined}
                        onClick={() => loadTypeData(type)}
                        sx={{
                            minHeight: 44,
                            fontWeight: selectedType === type ? 600 : 400,
                            fontSize: '0.82rem',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            transition,
                            ...(selectedType === type
                                ? {
                                    backgroundColor: ACCENT,
                                    color: '#0a1a1a',
                                    boxShadow: `0 2px 12px ${ACCENT_GLOW}`,
                                    '&:hover': { backgroundColor: ACCENT },
                                }
                                : {
                                    backgroundColor: 'rgba(255,255,255,0.06)',
                                    color: TEXT_SECONDARY,
                                    border: `1px solid ${SURFACE_BORDER}`,
                                    '&:hover': { backgroundColor: SURFACE_HOVER, color: TEXT_PRIMARY },
                                }),
                            ...reduceMotionSx,
                        }}
                    />
                ))}
            </Box>

            {/* Deal tabs: Global / Stage */}
            {selectedType === 'deal' && !loading && (
                <Tabs
                    value={dealTab}
                    data-testid="deal-tabs"
                    onChange={(_, v) => {
                        setDealTab(v);
                        if (v === 1 && !selectedPipeline && pipelines.length > 0) {
                            const firstPipeline = pipelines[0];
                            setSelectedPipeline(firstPipeline);
                            if (firstPipeline.stages?.length > 0) {
                                const firstStage = firstPipeline.stages[0];
                                setSelectedStage(firstStage);
                                loadStageProperties(firstPipeline, firstStage);
                            }
                        }
                    }}
                    sx={{
                        '& .MuiTab-root': { color: TEXT_TERTIARY, textTransform: 'none', fontWeight: 500, minHeight: 36 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    <Tab label={t('globalProperties')} data-testid="deal-tab-global" />
                    <Tab icon={<AccountTreeIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('stageProperties')} data-testid="deal-tab-stage" />
                </Tabs>
            )}

            {/* Stage mode: pipeline + stage selectors */}
            {isStageMode && !loading && (
                <Stack spacing={2}>
                    {/* Pipeline chips */}
                    <Box>
                        <Typography variant="caption" sx={{ color: TEXT_TERTIARY, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.08em', mb: 1, display: 'block' }}>
                            {t('selectPipelineForProps')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }} data-testid="pipeline-selector">
                            {pipelines.map((pl) => (
                                <Chip
                                    key={pl.id}
                                    label={pl.label || pl.id}
                                    onClick={() => onSelectPipeline(pl)}
                                    data-testid={`pipeline-chip-${pl.id}`}
                                    aria-current={selectedPipeline?.id === pl.id ? 'true' : undefined}
                                    sx={{
                                        fontWeight: 500, fontSize: '0.82rem', minHeight: 44, borderRadius: '10px',
                                        transition: 'all 0.15s ease', cursor: 'pointer',
                                        ...(selectedPipeline?.id === pl.id
                                            ? { backgroundColor: ACCENT, color: '#0a1a1a', '&:hover': { backgroundColor: ACCENT } }
                                            : { backgroundColor: 'rgba(255,255,255,0.06)', color: TEXT_SECONDARY, border: `1px solid ${SURFACE_BORDER}`, '&:hover': { backgroundColor: SURFACE_HOVER } }),
                                        ...reduceMotionSx,
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Stage chips */}
                    {selectedPipeline && (
                        <Box>
                            <Typography variant="caption" sx={{ color: TEXT_TERTIARY, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.08em', mb: 1, display: 'block' }}>
                                {t('selectStageForProps')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }} data-testid="stage-selector">
                                {(selectedPipeline?.stages || []).map((st) => (
                                    <Chip
                                        key={st.id}
                                        label={st.label || st.id}
                                        onClick={() => onSelectStage(st)}
                                        data-testid={`stage-chip-${st.id}`}
                                        aria-current={selectedStage?.id === st.id ? 'true' : undefined}
                                        sx={{
                                            fontWeight: 500, fontSize: '0.82rem', minHeight: 44, borderRadius: '10px',
                                            transition: 'all 0.15s ease', cursor: 'pointer',
                                            ...(selectedStage?.id === st.id
                                                ? { backgroundColor: 'rgba(118,169,172,0.3)', color: TEXT_PRIMARY, border: `1px solid ${ACCENT}`, '&:hover': { backgroundColor: 'rgba(118,169,172,0.35)' } }
                                                : { backgroundColor: 'rgba(255,255,255,0.04)', color: TEXT_TERTIARY, border: '1px solid rgba(255,255,255,0.06)', '&:hover': { backgroundColor: SURFACE_HOVER } }),
                                            ...reduceMotionSx,
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Stack>
            )}

            {/* Search-to-add property (single-select, always resets) */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: ACCENT }} />
                </Box>
            ) : (isStageMode && (!selectedPipeline || !selectedStage)) ? null : (
                <>
                    <Typography variant="caption" sx={{ color: TEXT_TERTIARY, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.08em', display: 'block' }}>
                        {t('selectProperties')}
                        {isStageMode && selectedPipeline && selectedStage && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: TEXT_DISABLED, textTransform: 'none' }}>
                                — {selectedPipeline.label} / {selectedStage.label}
                            </Typography>
                        )}
                    </Typography>
                    {stageLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} sx={{ color: ACCENT }} />
                        </Box>
                    ) : (
                        <Autocomplete
                            key={isStageMode ? stageKey : selectedType}
                            data-testid={isStageMode ? 'stage-properties-autocomplete' : 'property-search-add'}
                            value={null}
                            onChange={handleAddProperty}
                            options={allProperties.filter(p => !activeProps.some(ap => ap.name === p.name))}
                            getOptionLabel={(option) => option.label || option.name}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                            renderInput={(params) => <TextField {...params} size="small" placeholder={t('searchAndAddProperty')} />}
                            slotProps={darkPopperProps}
                            sx={inputSx}
                            blurOnSelect
                            clearOnBlur
                        />
                    )}
                </>
            )}

            {/* Property configuration rows (compact) */}
            {!loading && (
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <SettingsSuggestIcon sx={{ color: TEXT_SECONDARY, fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ color: TEXT_SECONDARY, fontWeight: 600 }}>
                            {t('configureProperties')}
                            {isStageMode && selectedPipeline && selectedStage && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: TEXT_DISABLED }}>
                                    ({selectedPipeline.label} → {selectedStage.label})
                                </Typography>
                            )}
                        </Typography>
                        {activeProps.length > 0 && (
                            <Chip label={activeProps.length} size="small"
                                sx={{ backgroundColor: 'rgba(118,169,172,0.3)', color: 'white', height: 22, fontSize: '0.75rem' }} />
                        )}
                    </Stack>

                    {/* Empty states */}
                    {isStageMode && (!selectedPipeline || !selectedStage) ? (
                        <Box data-testid="empty-state-stage" sx={{ p: 5, textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.02)', border: `2px dashed ${SURFACE_BORDER}`, borderRadius: '16px' }}>
                            <AccountTreeIcon sx={{ fontSize: 40, color: TEXT_HINT, mb: 1 }} />
                            <Typography sx={{ color: TEXT_DISABLED, mb: 0.5 }}>
                                {t('selectStageFirst')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: TEXT_HINT }}>
                                {t('stageEmptyHint')}
                            </Typography>
                        </Box>
                    ) : stageLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} sx={{ color: ACCENT }} />
                        </Box>
                    ) : activeProps.length === 0 ? (
                        <Box data-testid="empty-state-properties" sx={{ p: 5, textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.02)', border: `2px dashed ${SURFACE_BORDER}`, borderRadius: '16px' }}>
                            <SettingsSuggestIcon sx={{ fontSize: 40, color: TEXT_HINT, mb: 1 }} />
                            <Typography sx={{ color: TEXT_DISABLED, mb: 0.5 }}>
                                {t('noPropertiesSelected')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: TEXT_HINT }}>
                                {t('propertiesEmptyHint')}
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={0.75}>
                            {activeProps.map((prop, idx) => (
                                <PropertyRow
                                    key={prop.name}
                                    prop={prop}
                                    idx={idx}
                                    activeProps={activeProps}
                                    onToggleMandatory={toggleMandatory}
                                    onChangeMandatoryIf={changeMandatoryIf}
                                    onRemove={removeProperty}
                                />
                            ))}
                        </Stack>
                    )}
                </Box>
            )}

            {/* Save button */}
            <Tooltip title={!canSave ? t('selectPipelineAndStageFirst') : ''} placement="top" arrow>
                <span>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!canSave || saving || activeProps.length === 0}
                        startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
                        fullWidth
                        data-testid="manual-save"
                        aria-label={t('saveProperties')}
                        sx={{
                            minHeight: 44, py: 1.5, borderRadius: '12px', fontWeight: 700, fontSize: '1rem',
                            textTransform: 'none', letterSpacing: '0.02em',
                            boxShadow: `0 4px 20px ${ACCENT_GLOW}`,
                            background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
                            '&:hover': { background: 'linear-gradient(135deg, #8ec7c7 0%, #6aabab 100%)', boxShadow: '0 6px 24px rgba(118,169,172,0.4)', transform: 'translateY(-1px)' },
                            '&:active': { transform: 'translateY(0)' },
                            '&.Mui-disabled': { background: 'rgba(255,255,255,0.1)', color: TEXT_HINT },
                            transition: 'all 0.2s ease',
                            ...reduceMotionSx,
                        }}
                    >
                        {t('saveProperties')}
                    </Button>
                </span>
            </Tooltip>
        </Stack>
    );
}

// ─── Main PropertyChat Component ────────────────────────────────
export default function PropertyChat() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState(0);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [pendingActions, setPendingActions] = useState(null);
    const [hasDomainInteracted, setHasDomainInteracted] = useState(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const {
        isTyping,
        isApplying,
        applyFailed,
        snackbar,
        setSnackbar,
        sendMessage,
        applyActions,
        retryApply,
    } = usePropertyChatSocket({ t, setMessages, setPendingActions });

    // Admin role guard + intro bootstrap
    useEffect(() => {
        let mounted = true;
        Api.getUserData().then((response) => {
            if (!response?.role?.includes('admin')) {
                navigate('/app');
                return;
            }

            Api.getPropertyChatIntro().then((introResponse) => {
                if (!mounted) return;
                const introText = typeof introResponse?.intro === 'string' && introResponse.intro.trim()
                    ? introResponse.intro.trim()
                    : t('propertyChatWelcome');
                setHasDomainInteracted(!!introResponse?.hasDomainInteracted);
                setMessages([{
                    role: 'assistant',
                    content: introText,
                    timestamp: Date.now(),
                    introGenerated: !!introResponse?.intro,
                    introSource: introResponse?.source || 'none',
                }]);
            }).catch(() => {
                if (!mounted) return;
                setHasDomainInteracted(true);
                setMessages([{
                    role: 'assistant',
                    content: t('propertyChatWelcome'),
                    timestamp: Date.now(),
                    introGenerated: false,
                    introSource: 'none',
                }]);
            });
        }).catch(() => navigate('/app'));

        return () => {
            mounted = false;
        };
    }, [navigate, t]);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    const sendChatMessage = (text) => {
        const sent = sendMessage(text);
        if (sent) {
            setInput('');
            setHasDomainInteracted(true);
        }
        return sent;
    };

    const handleSend = () => {
        sendChatMessage(input);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleVoiceResult = (text) => {
        if (text) sendChatMessage(text);
    };

    // Editable suggestion handlers
    const handleToggleMandatory = (objectType, propertyName) => {
        setPendingActions(prev => {
            const safePrev = normalizeActions(prev);
            return safePrev.map(action => {
                if (action.objectType === objectType) {
                    const newProps = asArray(action.properties).map((prop) => (
                        prop.name === propertyName
                            ? { ...prop, mandatory: !prop.mandatory }
                            : prop
                    ));
                    return { ...action, properties: newProps };
                }
                return action;
            });
        });
    };

    const handleRemoveFromSuggestion = (objectType, propertyName) => {
        setPendingActions(prev => {
            const safePrev = normalizeActions(prev);
            return safePrev.map(action => {
                if (action.objectType === objectType) {
                    return { ...action, properties: asArray(action.properties).filter((prop) => prop.name !== propertyName) };
                }
                return action;
            }).filter(action => action.properties.length > 0);
        });
    };

    const NAV_HEIGHT = COLLAPSED_HEIGHT + 8;
    const fallbackHeight = `calc(100vh - ${NAV_HEIGHT}px)`;
    const dynamicHeight = `calc(100dvh - ${NAV_HEIGHT}px)`;

    return (
        <Stack
            sx={{
                // Fallback for mobile browsers that still don't support dvh.
                height: fallbackHeight,
                maxHeight: fallbackHeight,
                minHeight: 0,
                overflow: 'hidden',
                '@supports (height: 100dvh)': {
                    height: dynamicHeight,
                    maxHeight: dynamicHeight,
                },
            }}
        >
            {/* Header */}
            <Box sx={{
                px: 2, pt: 2, pb: 1,
                backgroundColor: 'rgba(16, 26, 34, 0.94)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: TEXT_PRIMARY, fontSize: '1.1rem' }}>
                    {t('propertyChatTitle')}
                </Typography>
                <Tabs
                    value={tabIndex}
                    onChange={(_, v) => setTabIndex(v)}
                    sx={{
                        mt: 1, minHeight: 36,
                        '& .MuiTab-root': {
                            minHeight: 36, textTransform: 'none', fontSize: '0.85rem',
                            color: TEXT_SECONDARY, fontWeight: 500,
                            '&.Mui-selected': { color: ACCENT, fontWeight: 600 },
                        },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    <Tab
                        icon={<SmartToyOutlined sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={t('chatTab')}
                        data-testid="tab-chat"
                        aria-label={t('chatTab')}
                    />
                    <Tab
                        icon={<TuneIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={t('manualTab')}
                        data-testid="tab-manual"
                        aria-label={t('manualTab')}
                    />
                </Tabs>
            </Box>

            {/* Tab content */}
            {tabIndex === 0 ? (
                // ─── Chat Tab ─────────────────────────────
                <>
                    {/* Messages */}
                    <Box
                        role="log"
                        aria-live="polite"
                        data-body-scroll-lock-ignore="true"
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            overscrollBehaviorY: 'contain',
                            px: 2,
                            py: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Stack spacing={1.5}>
                            {messages.map((msg, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                    {/* Welcome message card (first assistant message) */}
                                    {msg.role === 'assistant' && i === 0 ? (
                                        <Box sx={{
                                            ...glassCard,
                                            borderRadius: '16px',
                                            border: `1px solid ${ACCENT}30`,
                                            p: 2,
                                            maxWidth: '100%',
                                        }}>
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                <SmartToyOutlined sx={{ color: ACCENT, fontSize: 22 }} />
                                                <Typography variant="subtitle2" sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                                                    {t('propertyChatTitle')}
                                                </Typography>
                                            </Stack>
                                            <MarkdownMessage content={msg.content} textColor={TEXT_SECONDARY} />
                                            {msg.introGenerated && (
                                                <Typography variant="caption" sx={{ color: TEXT_HINT, mt: 1, display: 'block' }}>
                                                    {msg.introSource === 'today'
                                                        ? t('propertyChatDailyIntroToday')
                                                        : t('propertyChatDailyIntroCached')}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box sx={{
                                            maxWidth: '85%',
                                            ...(msg.role === 'user' ? {
                                                backgroundColor: ACCENT_DARK,
                                                color: '#fff',
                                                borderRadius: '16px 16px 4px 16px',
                                                px: 2, py: 1,
                                            } : {
                                                ...glassCard,
                                                borderRadius: '16px 16px 16px 4px',
                                                p: 1.5,
                                            }),
                                        }}>
                                            {msg.role === 'assistant' ? (
                                                <MarkdownMessage content={msg.content} textColor={TEXT_PRIMARY} />
                                            ) : (
                                                <Typography variant="body2" sx={{ color: TEXT_PRIMARY, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                                    {msg.content}
                                                </Typography>
                                            )}

                                            {/* Preview cards */}
                                            {(() => {
                                                const previewData = (asArray(msg.actions).length > 0 && asArray(pendingActions).length > 0)
                                                    ? previewFromActions(pendingActions)
                                                    : normalizePreview(msg.preview);
                                                return Object.keys(previewData).length > 0;
                                            })() && (
                                                <Box sx={{ mt: 1 }}>
                                                    {Object.entries((asArray(msg.actions).length > 0 && asArray(pendingActions).length > 0) ? previewFromActions(pendingActions) : normalizePreview(msg.preview)).map(([objType, props]) => (
                                                        <PropertyPreviewCard
                                                            key={objType}
                                                            objectType={objType}
                                                            properties={props}
                                                            onToggleMandatory={handleToggleMandatory}
                                                            onRemove={handleRemoveFromSuggestion}
                                                            allPropertyNames={asArray(props).map(p => p.name)}
                                                            editable={asArray(msg.actions).length > 0 && asArray(pendingActions).length > 0}
                                                        />
                                                    ))}
                                                </Box>
                                            )}

                                            {/* Action buttons */}
                                            {asArray(msg.actions).length > 0 && asArray(pendingActions).length > 0 && (
                                                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => applyActions(pendingActions)}
                                                        startIcon={<CheckCircleOutlineIcon />}
                                                        data-testid="apply-config"
                                                        aria-label={t('applyConfiguration')}
                                                        disabled={isApplying}
                                                        sx={{ minHeight: 44, flex: 1, textTransform: 'none', fontWeight: 600 }}
                                                    >
                                                        {isApplying ? t('applying', 'Applying...') : t('applyConfiguration')}
                                                    </Button>
                                                    {applyFailed && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => retryApply(pendingActions)}
                                                            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
                                                        >
                                                            {t('retry', 'Retry')}
                                                        </Button>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            ))}

                            {/* Suggested prompts — shown after welcome, before user's first message */}
                            {messages.length === 1 && !isTyping && !hasDomainInteracted && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 0.5 }}>
                                    {[t('chatPrompt1'), t('chatPrompt2'), t('chatPrompt3')].map((prompt) => (
                                        <Chip
                                            key={prompt}
                                            label={prompt}
                                            onClick={() => sendChatMessage(prompt)}
                                            data-testid={`quick-prompt-${prompt}`}
                                            aria-label={prompt}
                                            sx={{
                                                minHeight: 44, fontSize: '0.82rem', cursor: 'pointer',
                                                backgroundColor: 'rgba(118,169,172,0.12)',
                                                color: ACCENT,
                                                border: `1px solid rgba(118,169,172,0.3)`,
                                                borderRadius: '20px',
                                                transition, ...noMotion,
                                                '&:hover': { backgroundColor: 'rgba(118,169,172,0.22)', borderColor: ACCENT },
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}

                            {/* Typing indicator */}
                            {isTyping && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <Box sx={{ ...glassCard, borderRadius: '16px 16px 16px 4px', p: 1.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: ACCENT, animation: 'pulse 1s infinite', '@keyframes pulse': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }} />
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: ACCENT, animation: 'pulse 1s infinite 0.2s', '@keyframes pulse': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }} />
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: ACCENT, animation: 'pulse 1s infinite 0.4s', '@keyframes pulse': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }} />
                                    </Box>
                                </Box>
                            )}
                            <div ref={messagesEndRef} />
                        </Stack>
                    </Box>

                    {/* Input area */}
                    <Box sx={{
                        px: 2, py: 1.5,
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                        backgroundColor: 'rgba(16, 26, 34, 0.94)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                            <VoiceInput value="" onResult={handleVoiceResult} />
                            <TextField
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t('propertyChatPlaceholder')}
                                multiline
                                maxRows={3}
                                size="small"
                                data-testid="chat-input"
                                aria-label={t('propertyChatPlaceholder')}
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '14px',
                                        backgroundColor: 'rgba(255,255,255,0.06)',
                                    },
                                }}
                            />
                            <IconButton
                                onClick={handleSend}
                                disabled={!input.trim() || isTyping}
                                data-testid="chat-send"
                                aria-label={t('send', 'Send')}
                                sx={{
                                    minWidth: 44, minHeight: 44,
                                    color: input.trim() ? ACCENT : TEXT_TERTIARY,
                                    transition, ...noMotion,
                                }}
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </>
            ) : (
                // ─── Manual Tab ───────────────────────────
                <Box
                    data-body-scroll-lock-ignore="true"
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehaviorY: 'contain',
                        px: 2,
                        py: 2,
                    }}
                >
                    <ManualPanel />
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Stack>
    );
}
